<script setup lang="ts">
import { PhClock, PhGauge, PhMapPinLine } from '@phosphor-icons/vue'
import { computed, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppScreen from '@/components/layout/AppScreen.vue'
import MapControls from '@/components/map/MapControls.vue'
import type { MapMode } from '@/components/map/mapMode'
import MapView from '@/components/map/MapView.vue'
import { Button } from '@/components/ui/button'
import { useMapControls } from '@/composables/useMapControls'
import { useRouteVehiclesQuery } from '@/composables/useRouteVehiclesQuery'
import { useTransitIndex } from '@/composables/useTransitIndex'
import type { MapVehicle } from '@/types'
import { formatDistance, formatEta } from '@/utils/format'
import { findTrackedVehicle } from '@/utils/vehicleLookup'
import { resolveVehicleSnapshot } from '@/utils/vehicleSnapshot'

const props = defineProps<{ routeCode: string; vehicleId: string }>()

const route = useRoute()
const router = useRouter()
const { index } = useTransitIndex()
const {
  basemap,
  toggleBasemap,
  location: userLocation,
  isLocating,
  error: locationError,
  locate,
} = useMapControls()

const busRoute = computed(() => index.value?.routesByCode.get(props.routeCode))

const targetStop = computed(() => {
  const raw = route.query.stop
  const id = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isSafeInteger(id) && id > 0 ? index.value?.stopsById.get(id) : undefined
})

const query = useRouteVehiclesQuery(busRoute)

const snapshot = computed(() =>
  resolveVehicleSnapshot({
    vehicles: query.data.value ?? [],
    isPending: query.isPending.value,
    hasError: Boolean(query.error.value),
  }),
)

const found = computed(() => findTrackedVehicle(snapshot.value.vehicles, props.vehicleId))

/**
 * Last known position of the tracked bus.
 *
 * The user deliberately chose this vehicle. Blanking the screen — or worse,
 * navigating away — because one refresh missed it is a far worse outcome than
 * a marker that is briefly stale, so the last sighting is held and marked.
 */
const lastSeen = shallowRef<MapVehicle>()
watch(found, (vehicle) => {
  if (vehicle) lastSeen.value = vehicle
})

const vehicle = computed(() => found.value ?? lastSeen.value)
const isStale = computed(() => !found.value && lastSeen.value !== undefined)

const mode = computed<MapMode>(() =>
  busRoute.value
    ? {
        kind: 'vehicle',
        route: busRoute.value,
        vehicle: vehicle.value,
        targetStop: targetStop.value,
      }
    : { kind: 'stops', stops: [] },
)

const tripLabel = computed(() => {
  const direction = vehicle.value?.direction ?? 'outbound'
  const stops = busRoute.value?.directions[direction].stops
  if (!stops || stops.length < 2) return busRoute.value?.name ?? ''
  return `${stops[0]!.name} → ${stops.at(-1)!.name}`
})

const missing = computed(() => !vehicle.value && snapshot.value.source !== 'checking')

watch(
  [index, busRoute],
  ([loadedIndex, found]) => {
    if (loadedIndex && !found) router.replace({ name: 'track' })
  },
  { immediate: true },
)

function cancel() {
  if (router.options.history.state.back) router.back()
  else router.replace({ name: 'route', params: { routeCode: props.routeCode } })
}
</script>

<template>
  <AppScreen :title="`Route ${routeCode}`" leading="back">
    <div class="relative h-full">
      <MapView :mode="mode" :basemap="basemap" :user-location="userLocation" />

      <MapControls
        :basemap="basemap"
        :is-locating="isLocating"
        :error="locationError"
        @toggle-basemap="toggleBasemap"
        @locate="locate"
      />

      <p
        v-if="isStale"
        class="absolute inset-x-0 top-0 z-10 bg-background/95 px-3 py-2 text-xs text-muted-foreground ring-1 ring-foreground/25"
      >
        This bus has dropped out of the live feed — showing its last known position.
      </p>

      <p
        v-else-if="missing"
        class="absolute inset-x-0 top-0 z-10 bg-background/95 px-3 py-2 text-xs text-muted-foreground ring-1 ring-foreground/25"
      >
        This bus is not currently running.
      </p>
    </div>

    <template #footer>
      <div class="px-3 py-3">
        <div class="flex items-center justify-between gap-3">
          <p class="truncate text-base font-bold text-primary">
            {{ vehicle?.plate ?? vehicle?.label ?? '—' }}
          </p>
          <Button variant="outline" size="sm" type="button" @click="cancel">Cancel</Button>
        </div>

        <p class="mt-1 truncate text-xs text-muted-foreground">{{ tripLabel }}</p>

        <div class="mt-2 grid grid-cols-3 divide-x border-t pt-2 text-xs">
          <span class="flex items-center justify-center gap-1">
            <PhGauge class="size-4" />
            {{ vehicle?.speed !== undefined ? `${vehicle.speed} km/h` : '—' }}
          </span>
          <span class="flex items-center justify-center gap-1">
            <PhMapPinLine class="size-4" />
            {{
              vehicle?.distanceToStop !== undefined ? formatDistance(vehicle.distanceToStop) : '—'
            }}
          </span>
          <span class="flex items-center justify-center gap-1">
            <PhClock class="size-4" />
            {{
              vehicle?.minutesToNextStop !== undefined
                ? `${formatEta(vehicle.minutesToNextStop)} min`
                : '—'
            }}
          </span>
        </div>

        <p v-if="targetStop" class="mt-2 truncate text-xs text-muted-foreground">
          Measured to {{ targetStop.name }}
        </p>
      </div>
    </template>
  </AppScreen>
</template>
